import { useEffect, useRef } from 'react'
import { scrollToModal } from '../utils/modalUtils'

/**
 * Hook pour gérer le focus dans les modals, optimisé pour mobile
 */
export const useModalFocus = (isOpen, onClose) => {
  const modalRef = useRef(null)
  const previousActiveElement = useRef(null)
  const focusableElementsRef = useRef([])

  useEffect(() => {
    if (!isOpen) return

    // Sauvegarder l'élément actuellement focalisé
    previousActiveElement.current = document.activeElement

    // Fonction pour gérer la navigation au clavier
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }

      if (e.key === 'Tab') {
        e.preventDefault()
        
        const focusableElements = modalRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) || []

        if (focusableElements.length === 0) return

        const currentIndex = Array.from(focusableElements).indexOf(document.activeElement)
        let nextIndex

        if (e.shiftKey) {
          // Shift + Tab : aller au précédent
          nextIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1
        } else {
          // Tab : aller au suivant
          nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0
        }

        focusableElements[nextIndex]?.focus()
      }
    }

    // Ajouter les event listeners
    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'

    // Focus sur le modal après un délai pour laisser l'animation se terminer
    const focusTimeout = setTimeout(() => {
      if (modalRef.current) {
        // Scroll vers le modal pour s'assurer qu'il est visible
        // Plus agressif sur desktop pour s'assurer que le modal est visible
        const isMobile = window.innerWidth <= 768
        scrollToModal(modalRef.current, {
          behavior: 'smooth',
          block: isMobile ? 'center' : 'start', // Sur desktop, scroll vers le haut
          inline: 'nearest'
        })

        // Si on est en bas de page sur desktop, scroll vers le haut
        if (!isMobile && window.pageYOffset > window.innerHeight) {
          setTimeout(() => {
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            })
          }, 100)
        }

        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )

        if (focusableElements.length > 0) {
          // Focus sur le premier élément focusable
          focusableElements[0].focus()
        } else {
          // Si aucun élément focusable, focus sur le modal lui-même
          modalRef.current.focus()
        }
      }
    }, 150) // Délai légèrement plus long pour mobile

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'
      clearTimeout(focusTimeout)

      // Restaurer le focus sur l'élément précédent
      if (previousActiveElement.current) {
        previousActiveElement.current.focus()
      }
    }
  }, [isOpen, onClose])

  return modalRef
}

export default useModalFocus
